import React, { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { usersApi, type ReferralStats } from "../services/users";
import { ordersApi } from "../services/orders";
import { openMoneyGate, whichGate } from "../lib/connectBank";
import { takesMoney } from "../utils/takesMoney";
import { useLinkFlag } from "../hooks/useLinkFlag";
import { pageCache } from "../lib/pageCache";
import ReferEarnSheet, { type TierJump } from "./ReferEarnSheet";
import type { FeeTierStatus } from "./FeeTierSheet";
import { SHOW_TIERS } from "../lib/tiersPaused";
import type { User } from "../types/User";

// The talent onboarding emails' buttons that open a sheet on arrival. The link
// lands on /requests?<flag>=1 and the flag leaves the URL at once
// (hooks/useLinkFlag, the same mechanism as ReviewSheet's ?verify=1). The sheet
// then opens only if it is this talent's next step, read fresh from the
// server; in every other state the page simply shows (an old email, a talent
// who already did it, a declined applicant): no sheet, no error.
//
//   ?payouts=1  Connect Bank, while the money gate's answer is "bank": the
//               talent can't take money yet AND has no bank (lib/connectBank).
//   ?refer=1    Refer & Earn, when the Profile drawer would open it too.
//
// Mounted next to the two money-gate sheets on every signed-in page
// (RouteGuards.RequireAuth). Without a flag it makes no server call at all.

/** The bank is the next step — the same test Balance and Edit profile use for their bank lock. */
const bankIsNext = (p: User): boolean => !takesMoney(p) && whichGate(p) === "bank";

/** ProfilePage mounts its ReferEarnSheet on this rule (showRefer && !referLocked): the server's word, and the money rule. */
const referEarnOpens = (stats: ReferralStats | null, p: User): boolean => !!stats?.eligible && takesMoney(p);

/** "Refer 5 talent → jump a tier" (1.5.1 #11) from the fee-tier payload, built as ProfilePage builds it. */
const tierJumpFrom = (tier: FeeTierStatus | null): TierJump | null =>
  tier?.enabled && tier.referralBoost
    ? { ...tier.referralBoost, nextName: tier.next?.name ?? null, tierName: tier.tier?.name ?? null }
    : null;

export default function EmailLinkSheets() {
  const { user } = useContext(AuthContext);
  const slug = user?.slug;
  const navigate = useNavigate();
  const [refer, setRefer] = useState<{ stats: ReferralStats; tierJump: TierJump | null; profile: User } | null>(null);

  useLinkFlag("payouts", () => {
    if (!slug) return;
    // The profile rides along so the bank sheet opens on the right face (Stripe or MassPay) at once.
    usersApi
      .get(slug)
      .then((p) => {
        if (bankIsNext(p)) openMoneyGate(p);
      })
      .catch(() => {});
  });

  useLinkFlag("refer", () => {
    if (!slug) return;
    // No fee tier (tiers paused or off, an older server, a failed read) = the sheet without the tier-jump card.
    const tierRead = SHOW_TIERS ? ordersApi.feeTier().catch(() => null) : Promise.resolve(null);
    Promise.all([usersApi.get(slug), usersApi.referrals(), tierRead])
      .then(([p, stats, tier]) => {
        if (referEarnOpens(stats, p)) setRefer({ stats, tierJump: tierJumpFrom(tier), profile: p });
      })
      .catch(() => {});
  });

  if (!refer) return null;
  // On <body>, like the money-gate sheets: above the tab bar and any drawer.
  return createPortal(
    <ReferEarnSheet
      open
      onClose={() => setRefer(null)}
      stats={refer.stats}
      onCodeChanged={(code) => setRefer({ ...refer, stats: { ...refer.stats, code } })}
      // "Add to my balance" (1.5.2): the same three hand-offs as the Profile
      // page's sheet — fresh numbers after an add (and for Profile's next
      // paint), the money gate for no bank, "See Balance" → the Balance tab.
      onCashoutChanged={(cashout) => {
        const stats = { ...refer.stats, cashout };
        if (!cashout) delete stats.cashout;
        if (slug) pageCache.set(`referrals:${slug}`, stats);
        setRefer({ ...refer, stats });
      }}
      onConnectBank={() => {
        setRefer(null);
        openMoneyGate(refer.profile);
      }}
      onSeeBalance={() => {
        setRefer(null);
        navigate("/balance");
      }}
      tierJump={refer.tierJump}
    />,
    document.body
  );
}
